import { useRef } from 'react';
import BranchNotice from './branch-notice';
import NumberSearchPad from './number-pad';
import ParkingContent from './parking-content';
import { SearchResults } from './search-results';
import ParkingDetailScreen from './search-results/ParkingDetailScreen';
import type {
    ParkingRecord,
    AreaInfoDetail,
    ParkingFeeApiResponse,
    ParkingInApiResponse,
    BranchParkingConfig
} from '@/services/dto/parking';
import type { ApiResponse } from '@/types/api';

interface KoreanMainContentProps {
    searchState: {
        selectedResult: ParkingRecord | null;
        showResults: boolean;
        query: string;
    };
    handleBackFromDetail: () => void;
    handleCardClick: (result: ParkingRecord) => void;
    handleSearch: (query: string) => void;
    handleClearAll: () => void;
    handleBackspace: () => void;
    isUseLocation: boolean;
    areaInfo: AreaInfoDetail | null;
    parkingFeeApiData: ApiResponse<ParkingFeeApiResponse> | null;
    parkingApiData: ApiResponse<ParkingInApiResponse> | null;
    isSearchLoading: boolean;
    parkingData: ApiResponse<BranchParkingConfig> | null;
    isHorizontal: boolean;
    screenMode: 'split' | 'fullscreen';
    handleDirectionGuide: () => void;
    showBranchNotice?: boolean;
}

export default function KoreanMainContent({
    searchState,
    handleBackFromDetail,
    handleCardClick,
    handleSearch,
    handleClearAll,
    handleBackspace,
    isUseLocation,
    areaInfo,
    parkingFeeApiData,
    parkingApiData,
    isSearchLoading,
    parkingData,
    isHorizontal,
    screenMode,
    handleDirectionGuide,
    showBranchNotice = false,
}: KoreanMainContentProps) {
    const searchResultsRef = useRef<any>(null);
    const showNotice = showBranchNotice && !searchState.selectedResult;
    const isBranchFullscreen = showNotice && screenMode === 'fullscreen';
    const branchNotice = showNotice ? <BranchNotice /> : null;

    const handleSearchComplete = () => {
        // Focus on search results after search is complete
        searchResultsRef.current?.focusResults();
    };

    return (
        <>
            {searchState.selectedResult ? (
                <main
                    className={`widescreen:py-[calc(32*var(--vh-unit))] widescreen:fullscreen:py-[calc(32*var(--vh-unit))] fullscreen:py-[calc(72*var(--vh-unit))] widescreen:justify-center widescreen:items-start widescreen:gap-[calc(32*var(--vh-unit))] widescreen:shrink-0 widescreen:px-0 fullscreen:px-[calc(104*var(--vw-unit))] flex h-[calc(589*var(--vh-unit))] min-h-0 flex-1 flex-row items-center justify-between gap-0 py-[calc(24*var(--vh-unit))] pr-[calc(80*var(--vw-unit))] pl-[calc(104*var(--vw-unit))]`}
                >
                    <ParkingDetailScreen
                        rawData={parkingFeeApiData?.ok && parkingFeeApiData.data ? parkingFeeApiData.data : null}
                        result={searchState.selectedResult}
                        onBack={handleBackFromDetail}
                        isUseLocation={isUseLocation}
                        areaInfo={areaInfo}
                        parkingFeeApiData={parkingFeeApiData?.ok && parkingFeeApiData.data ? parkingFeeApiData.data.resultBody : null}
                        parkingFeeData={parkingData?.ok && parkingData.data ? parkingData.data : null}
                        handleDirectionGuide={handleDirectionGuide}
                    />
                </main>
            ) : (
                <main
                    className={`widescreen:py-[calc(32*var(--vh-unit))] widescreen:fullscreen:py-[calc(32*var(--vh-unit))] ${isBranchFullscreen ? 'py-[calc(40*var(--vh-unit))]' : 'fullscreen:py-[calc(72*var(--vh-unit))] py-[calc(24*var(--vh-unit))]'} widescreen:justify-center widescreen:items-start h-[calc(589*var(--vh-unit))] ${searchState.showResults ? 'widescreen:gap-[calc(80*var(--vh-unit))]' : 'widescreen:gap-[calc(80*var(--vh-unit))]'} widescreen:shrink-0 widescreen:px-0 flex min-h-0 flex-1 ${isBranchFullscreen ? 'flex-col items-stretch justify-start gap-[calc(40*var(--vh-unit))]' : 'flex-row items-center justify-between gap-[calc(72*var(--vh-unit))]'} px-[calc(104*var(--vw-unit))]`}
                >
                    {isBranchFullscreen && branchNotice}
                    {isBranchFullscreen ? (
                        <div className="flex min-h-0 w-full flex-1 flex-row items-stretch justify-between gap-[calc(72*var(--vh-unit))]">
                            {/* Left section */}
                            {searchState.showResults ? (
                                <section
                                    className={`widescreen:w-[calc(865*var(--vw-unit))] widescreen:fullscreen:h-[calc(730*var(--vh-unit))] relative ${isBranchFullscreen ? 'h-full min-h-0' : 'fullscreen:h-[calc(1235*var(--vh-unit))] h-full'} scrollbar-custom-gray-99938f scrollbar-w-4 flex w-[calc(400*var(--vw-unit))] flex-col items-center gap-[calc(40*var(--vh-unit))] self-stretch overflow-y-auto`}
                                >
                                    <SearchResults
                                        ref={searchResultsRef}
                                        searchQuery={searchState.query}
                                        onCardClick={handleCardClick}
                                        parkingApiData={parkingApiData?.ok && parkingApiData.data ? parkingApiData.data : null}
                                        isLoading={isSearchLoading}
                                    />
                                </section>
                            ) : (
                                <section
                                    className={`widescreen:w-[calc(865*var(--vw-unit))] widescreen:fullscreen:h-[calc(759*var(--vh-unit))] ${isBranchFullscreen ? 'h-full min-h-0' : 'fullscreen:h-[calc(1235*var(--vh-unit))] h-full'} widescreen:gap-[calc(60*var(--vh-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 flex w-[calc(400*var(--vw-unit))] flex-col items-center gap-[calc(40*var(--vh-unit))] self-stretch overflow-y-auto`}
                                >
                                    {showBranchNotice && screenMode !== 'fullscreen' && branchNotice}
                                    <ParkingContent parkingData={parkingData?.ok && parkingData.data ? parkingData.data : null} isHorizontal={isHorizontal} />
                                </section>
                            )}
                            {/* Right section */}
                            <section
                                className={`widescreen:w-[calc(458*var(--vw-unit))] fullscreen:widescreen:h-[calc(759*var(--vh-unit))] fullscreen:widescreen:py-[calc(52*var(--vh-unit))] flex w-[calc(400*var(--vw-unit))] items-start justify-center self-stretch rounded-[calc(12*var(--vh-unit))] border border-[var(--color-bg-gray-f4)] bg-[var(--color-bg-gray-f4-to-dark)] ${isBranchFullscreen ? 'h-full min-h-0 py-[calc(40*var(--vh-unit))]' : 'fullscreen:h-[calc(1235*var(--vh-unit))] fullscreen:py-[calc(80*var(--vh-unit))] h-full pt-[calc(40*var(--vh-unit))]'} widescreen:h-[calc(622*var(--vh-unit))]`}
                            >
                                <NumberSearchPad
                                    screenMode={screenMode}
                                    onSearch={handleSearch}
                                    onClearAll={handleClearAll}
                                    onBackspace={handleBackspace}
                                    onSearchComplete={handleSearchComplete}
                                />
                            </section>
                        </div>
                    ) : (
                        <>
                            {/* Left section */}
                            {searchState.showResults ? (
                                <section
                                    className={`widescreen:w-[calc(865*var(--vw-unit))] widescreen:fullscreen:h-[calc(730*var(--vh-unit))] fullscreen:h-[calc(1235*var(--vh-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 relative flex h-full w-[calc(400*var(--vw-unit))] flex-col items-center gap-[calc(40*var(--vh-unit))] self-stretch overflow-y-auto`}
                                >
                                    {showNotice && branchNotice}
                                    <SearchResults
                                        ref={searchResultsRef}
                                        searchQuery={searchState.query}
                                        onCardClick={handleCardClick}
                                        parkingApiData={parkingApiData?.ok && parkingApiData.data ? parkingApiData.data : null}
                                        isLoading={isSearchLoading}
                                    />
                                </section>
                            ) : (
                                <section
                                    className={`widescreen:w-[calc(865*var(--vw-unit))] widescreen:fullscreen:h-[calc(759*var(--vh-unit))] fullscreen:h-[calc(1235*var(--vh-unit))] widescreen:gap-[calc(60*var(--vh-unit))] scrollbar-custom-gray-99938f scrollbar-w-4 flex h-full w-[calc(400*var(--vw-unit))] flex-col items-center gap-[calc(40*var(--vh-unit))] self-stretch overflow-y-auto`}
                                >
                                    {showBranchNotice && branchNotice}
                                    <ParkingContent parkingData={parkingData?.ok && parkingData.data ? parkingData.data : null} isHorizontal={isHorizontal} />
                                </section>
                            )}
                            <section
                                className={`widescreen:w-[calc(458*var(--vw-unit))] fullscreen:widescreen:h-[calc(759*var(--vh-unit))] fullscreen:widescreen:py-[calc(52*var(--vh-unit))] fullscreen:h-[calc(1235*var(--vh-unit))] fullscreen:py-[calc(80*var(--vh-unit))] widescreen:h-[calc(622*var(--vh-unit))] flex h-full w-[calc(400*var(--vw-unit))] items-start justify-center self-stretch rounded-[calc(12*var(--vh-unit))] border border-[var(--color-bg-gray-f4)] bg-[var(--color-bg-gray-f4-to-dark)] pt-[calc(40*var(--vh-unit))]`}
                            >
                                <NumberSearchPad
                                    screenMode={screenMode}
                                    onSearch={handleSearch}
                                    onClearAll={handleClearAll}
                                    onBackspace={handleBackspace}
                                    onSearchComplete={handleSearchComplete}
                                />
                            </section>
                        </>
                    )}
                </main>
            )}
        </>
    );
}
